/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import type { PassRecommendation, WizardAnswers } from '../types/wizard.types';

/**
 * Maps wizard answers to pass recommendations.
 * Pure function with no side effects — fully unit-testable.
 */
export function getPassRecommendations(answers: WizardAnswers): PassRecommendation[] {
    const { residentAnswer, selectedAttributes, frequencyAnswer } = answers;

    // Non-resident path
    if (residentAnswer === false) {
        if (frequencyAnswer === 'multiple') {
            return [{ passType: 'nonresident' }];
        }
        // single location
        return [{ passType: 'annual' as never, isSitePass: true }];
    }

    // US resident path with specific attributes
    if (residentAnswer === true) {
        // If "none" selected or no attributes, check visit frequency
        const hasNone =
            selectedAttributes.includes('none') || selectedAttributes.length === 0;

        if (hasNone) {
            if (frequencyAnswer === 'multiple') {
                return [{ passType: 'resident' }];
            }
            // single location
            return [{ passType: 'annual' as never, isSitePass: true }];
        }

        // Build recommendations from selected attributes
        const recommendations: PassRecommendation[] = [];
        const seen = new Set<string>();

        for (const attr of selectedAttributes) {
            const recs = getRecommendationsForAttribute(attr);
            for (const rec of recs) {
                const key = rec.isSitePass ? 'site-pass' : rec.passType;
                if (!seen.has(key)) {
                    seen.add(key);
                    recommendations.push(rec);
                }
            }
        }

        return recommendations;
    }

    // Fallback — should not happen if wizard flow is followed correctly
    return [];
}

function getRecommendationsForAttribute(attribute: string): PassRecommendation[] {
    switch (attribute) {
        case 'fourth-grader':
            return [{ passType: 'fourth-grade' }];
        case 'permanent-disability':
            return [{ passType: 'access' }];
        case 'gold-star-veteran':
            return [{ passType: 'military-lifetime' }];
        case 'military-dependent':
            return [{ passType: 'military-annual' }];
        case 'senior':
            return [{ passType: 'senior-lifetime' }, { passType: 'senior-annual' }];
        default:
            return [];
    }
}
